import { Combobox } from '@base-ui/react/combobox'
import clsx from 'clsx'
import type { ReactNode, RefObject } from 'react'

import {
  type ComboboxClassNames,
  getOptionClassName,
  preventMouseDown,
} from './types'

type Props<T> = {
  classNames?: ComboboxClassNames<T>
  emptyMessage?: ReactNode
  getOptionLabel: (option: T) => string
  getOptionValue: (option: T) => string
  isLoading?: boolean
  popupHasContent: boolean
  portalContainer: HTMLElement | null | undefined
  renderOption?: (option: T) => ReactNode
  shouldHideOption?: (option: T) => boolean
  // Multi-only "create new" pseudo-option below the list.
  createLabel?: string
  onCreate?: () => void
  positionerAnchor?: RefObject<HTMLElement | null>
}

export function ComboboxDropdown<T>({
  classNames,
  emptyMessage,
  getOptionLabel,
  getOptionValue,
  isLoading,
  popupHasContent,
  portalContainer,
  renderOption,
  shouldHideOption,
  createLabel,
  onCreate,
  positionerAnchor,
}: Props<T>) {
  return (
    <Combobox.Portal container={portalContainer}>
      <Combobox.Positioner
        className={classNames?.positioner}
        sideOffset={4}
        anchor={positionerAnchor}
      >
        <Combobox.Popup
          className={clsx(
            'w-[var(--anchor-width)]',
            !popupHasContent && 'hidden',
            classNames?.popup,
          )}
        >
          {isLoading && (
            <Combobox.Status>
              <div className={classNames?.loading}>Loading…</div>
            </Combobox.Status>
          )}
          {!isLoading && emptyMessage && (
            <Combobox.Empty>
              <div className={classNames?.empty}>{emptyMessage}</div>
            </Combobox.Empty>
          )}
          <Combobox.List className={classNames?.list}>
            {(option: T) => {
              if (shouldHideOption?.(option)) return null
              return (
                <Combobox.Item
                  key={getOptionValue(option)}
                  value={option}
                  className={getOptionClassName(option, classNames)}
                >
                  {renderOption ? renderOption(option) : getOptionLabel(option)}
                </Combobox.Item>
              )
            }}
          </Combobox.List>
          {createLabel && onCreate && (
            <div
              role="option"
              className={classNames?.create}
              onMouseDown={preventMouseDown}
              onClick={onCreate}
            >
              Create "{createLabel}"
            </div>
          )}
        </Combobox.Popup>
      </Combobox.Positioner>
    </Combobox.Portal>
  )
}
